/**
 * cart-promo-revalidate.js
 *
 * Stale cart discount 자동 재계산 모듈 (2026-05-28).
 *
 * 책임:
 *   - 카트 진입 시 /cart/update.js no-op mutation 호출 → Shopify discount 재계산 강제
 *   - 재계산 결과로 cart-items section 갱신 (refreshCartSection)
 *
 * 배경:
 *   Shopify cart 는 discount_application snapshot 보유. Admin 에서 discount 삭제·만료 후
 *   사용자 cart 는 **자동 갱신 안 됨** — 다음 cart mutation (line +/-, add, update) 까지 stale.
 *   결과: 카트 페이지 표시 가격 ≠ checkout 재계산 가격 → 사용자 혼란·이탈 위험.
 *
 *   해결: 카트 진입 시 no-op mutation 1회로 Shopify 재계산 트리거.
 *
 * 무한 루프 차단:
 *   refreshCartSection → cart-items innerHTML swap → 새 hub 인스턴스 connectedCallback → 또 revalidate 호출 위험.
 *   module-level _revalidated flag 로 페이지 lifetime 1회만 실행.
 *   (페이지 reload = module state 초기화 → 다음 진입 시 재실행)
 *
 * 의존:
 *   - hub instance (refreshCartSection 호출)
 *
 * 로드 순서:
 *   1. cart-coupon-utils.js
 *   2. cart-promo-effects.js
 *   3. cart-promo-storefront.js
 *   4. cart-promo-bxgy.js
 *   5. cart-promo-cascade.js
 *   6. cart-promo-sync.js
 *   7. cart-promo-revalidate.js  ← 본 파일
 *   8. cart-coupon-hub.js
 *
 * 같은 layer 패턴:
 *   - assets/cart-stock-guard.js (카트 진입 시 sold-out 자동 제거)
 */
(function (global) {
  'use strict';

  // 🔴 무한 루프 차단 — refreshCartSection 후 hub 재마운트 → connectedCallback 재호출 시 skip.
  // 페이지 reload 시 자동 reset.
  let _revalidated = false;
  // 🔴 깨진 BXGY 그룹 자동복구도 페이지 lifetime 1회 (heal 의 refreshCartSection → 재마운트 → 재호출 차단).
  let _bxgyHealed = false;

  const Revalidate = {

    async revalidateCartDiscounts(hub) {
      if (_revalidated) return;
      _revalidated = true;

      // 🔴 즉시 mask — SSR paint 후 사용자가 stale 가격 보는 시간 최소화.
      //   refreshCartSection 가 innerHTML 만 교체 (cart-items element 그대로) → 클래스 유지됨 → unmask 안전.
      //   refreshCartSection 끝나면 unmask. network 실패 / hang 시 5초 fallback 으로 영원히 가려지지 않게.
      const cartItemsEl = document.querySelector('cart-items');
      cartItemsEl?.classList.add('is-cart-loading');

      // safety net — JS 실패해도 5초 후 자동 unmask
      const unmaskTimeout = setTimeout(() => {
        document.querySelector('cart-items')?.classList.remove('is-cart-loading');
      }, 5000);

      try {
        // 🔴 (2026-05-28) 깨진 BXGY 그룹 자동복구는 loadInitialData 에서 promo config 로드 후 호출
        //   (Revalidate.cleanupInconsistentBxgyGroups) — 수량 불일치 검사에 buy/get qty 필요.

        // no-op mutation — empty attributes set 으로 cart write 트리거.
        // Shopify 가 cart 저장 시 자동 재계산 → 삭제/만료된 discount 제거.
        const res = await fetch(MISTO.localeUrl('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: {} }),
        });
        if (!res.ok) {
          console.warn('[cart-promo-revalidate] mutation failed', res.status);
          return;
        }

        // SSR HTML 은 옛 cart 상태로 render 됨 → fresh sections fetch + swap.
        // refreshCartSection 이 cache: 'no-store' + cache buster 로 fresh 보장.
        await hub?.refreshCartSection();
      } catch (err) {
        // graceful — revalidate 실패해도 카트 동작 자체엔 영향 없음 (그저 stale 유지).
        console.warn('[cart-promo-revalidate] failed', err);
      } finally {
        clearTimeout(unmaskTimeout);
        // requestAnimationFrame — refreshCartSection 의 DOM swap 직후 다음 frame 에서 unmask.
        // cart-items element 동일 (innerHTML 만 교체) 이라 querySelector 안전.
        requestAnimationFrame(() => {
          document.querySelector('cart-items')?.classList.remove('is-cart-loading');
        });
      }
    },

    // 🔴 (2026-05-28) 깨진 BXGY 그룹 자동복구 — 카트 진입 1회 (loadInitialData 에서 호출).
    //   불일치 판정 (group_id 단위, cart.js SSOT):
    //     (a) gift(get) 있는데 anchor 없음 → orphan gifts
    //     (b) anchor 있는데 gift 없음 → orphan anchor
    //     (c) promo config 있으면: gift 수 ≠ floor(anchorBuyQty / buyQty) × getQty → 수량 불일치 (과선택/부족)
    //   처리: gift 라인 삭제 + anchor promo clear(→ No promotion). config 없으면 (a)(b) 구조 검사만.
    //   독립: group_id 로만 묶음, 정확 key 로만 mutation. plain 라인 미간섭.
    //   회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
    async cleanupInconsistentBxgyGroups(hub) {
      if (_bxgyHealed) return false;
      _bxgyHealed = true;
      try {
        const cartRes = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
        if (!cartRes.ok) return false;
        const cart = await cartRes.json();
        const items = cart.items || [];

        const groupKeyOf = (p) => (p && (p._promotion_group_id || p._promotion_id)) || '';
        const isAnchor = (p) => p && p._promotion_bxgy_anchor === 'true';
        const isGetChild = (p) =>
          p && p._promotion_bxgy_anchor !== 'true' &&
          (p._promotion_added_by_bxgy === 'true' || p._promotion_bxgy_role === 'get');

        // group_id 단위로 BXGY 라인 묶기
        const groups = new Map();
        for (const it of items) {
          const p = it.properties || {};
          if (p._promotion_type !== 'buy_x_get_y') continue;
          const gid = groupKeyOf(p);
          if (!gid) continue;
          if (!groups.has(gid)) groups.set(gid, []);
          groups.get(gid).push(it);
        }
        if (!groups.size) return false;

        const removeKeys = [];
        const clearAnchors = [];
        groups.forEach((lines) => {
          const anchors = lines.filter((l) => isAnchor(l.properties));
          const gifts = lines.filter((l) => isGetChild(l.properties));
          const anchorBuyQty = anchors.reduce((s, l) => s + (l.quantity || 0), 0);
          const giftQty = gifts.reduce((s, l) => s + (l.quantity || 0), 0);

          let inconsistent = false;
          if (anchors.length === 0) inconsistent = true;            // (a)
          else if (gifts.length === 0) inconsistent = true;          // (b)
          else {
            const cfg = Revalidate._findBxgyConfig(hub, anchors[0]);
            if (cfg) {
              const buyQty = Math.max(1, parseInt(cfg.buyQty, 10) || 1);
              const getQty = Math.max(1, parseInt(cfg.getQty, 10) || 1);
              const expected = Math.floor(anchorBuyQty / buyQty) * getQty;
              if (expected < 1 || giftQty !== expected) inconsistent = true; // (c)
            }
            // cfg 없으면 수량 검사 skip (구조 검사만) — stale promo 는 stale-discount revalidate 가 처리
          }

          if (inconsistent) {
            gifts.forEach((g) => removeKeys.push(g.key));
            anchors.forEach((a) => clearAnchors.push({ key: a.key, variantId: String(a.variant_id) }));
          }
        });

        if (!removeKeys.length && !clearAnchors.length) return false;

        if (removeKeys.length) {
          const updates = {};
          removeKeys.forEach((k) => { updates[k] = 0; });
          await fetch(MISTO.localeUrl('/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ updates }),
          });
        }
        for (const a of clearAnchors) {
          try {
            await global.CartPromoEffects.clearLineByKey(a.key, a.variantId);
          } catch (err) {
            console.warn('[cart-promo-revalidate] clearAnchor', a.key, err);
          }
        }
        await hub?.refreshCartSection();
        return true;
      } catch (err) {
        console.warn('[cart-promo-revalidate] cleanupInconsistentBxgyGroups', err);
        return false;
      }
    },

    // promo config 조회 — hub.state.promotions 에서 anchor 라인의 promo(buyQty/getQty) 찾기. 못 찾으면 null.
    _findBxgyConfig(hub, anchorLine) {
      if (!hub || !hub.state || !hub.state.promotions || !anchorLine) return null;
      const p = anchorLine.properties || {};
      const code = p._promotion_code || '';
      const pid = p._promotion_id || '';
      const buckets = [
        hub.state.promotions[String(anchorLine.variant_id)],
        hub.state.promotions[String(anchorLine.product_id)],
      ].filter(Boolean);
      for (const list of buckets) {
        const hit = (list || []).find(
          (pr) => pr && (pr.id === pid || pr.code === code || pr.id === code)
        );
        if (hit) return hit;
      }
      return null;
    },

    // 테스트·debug 용 — flag reset (사용처 없음, 수동 개입 시만).
    _reset() {
      _revalidated = false;
      _bxgyHealed = false;
    },
  };

  global.CartPromoRevalidate = Revalidate;
})(typeof window !== 'undefined' ? window : globalThis);
